import { Button, ResponsiveTable, Switch, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import type {
  Customer,
  CustomerDocumentRef,
  CustomerDocuments,
  CustomerJobRef,
} from './contractsMirror.js';
import { COLUMN_LABEL, FIELD_LABEL } from './labelStyles.js';
import { PORTAL_READY } from './portal.js';
import { useUpdateClientMutation } from './useClientsQuery.js';

/**
 * The client page (`/clients/:id`). Header (name, contact, address) + Edit, the
 * Customer-portal card (an ON/OFF switch + "Last seen: never" — the portal is a
 * switch, not an app: there is no portal tile and no portal page), the notes
 * card, and the three document sections (estimates, invoices, jobs) linking to
 * their existing pages. Money renders through the money formatters' cents side —
 * never divided by hand.
 *
 * Every section has its own empty copy, so a brand-new client page reads as
 * "nothing yet" rather than as broken.
 */

const SECTION = 'rounded-md border border-border bg-card p-4 sm:p-6';
const SECTION_HEADING = 'mb-3 font-display font-semibold text-foreground text-lg';
const EMPTY = 'text-muted-foreground text-sm';
const TH = `px-3 py-2 text-left ${COLUMN_LABEL}`;
const NUMERIC_TH = `px-3 py-2 text-right ${COLUMN_LABEL}`;

export interface ClientDetailViewProps {
  customer: Customer;
  documents: CustomerDocuments;
}

export function ClientDetailView({ customer, documents }: ClientDetailViewProps) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const update = useUpdateClientMutation(customer.id);

  function togglePortal(enabled: boolean) {
    // PUT takes the same body as create plus `portal_enabled?` — the toggle sends
    // the whole record so an out-of-date detail render cannot blank a field.
    update.mutate({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      notes: customer.notes,
      portal_enabled: enabled,
    });
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <Link
          to="/clients"
          className="mb-3 inline-flex min-h-11 items-center gap-1 rounded-md pr-3 font-medium text-muted-foreground text-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t('clients:actions.parentClients')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-semibold text-3xl text-foreground">{customer.name}</h1>
            <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
              <ContactItem label={t('clients:detail.email')} value={customer.email} />
              <ContactItem label={t('clients:detail.phone')} value={customer.phone} />
              <ContactItem label={t('clients:detail.address')} value={customer.address} />
            </dl>
          </div>
          <div className="flex max-w-full flex-nowrap items-center gap-2 overflow-x-auto pb-1 [&>*]:shrink-0">
            <Button asChild>
              <Link to="/clients/$id/edit" params={{ id: String(customer.id) }}>
                {t('clients:actions.edit')}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Two columns only while there are two cards; Notes alone takes the full width. */}
      <div className={PORTAL_READY ? 'grid gap-4 lg:grid-cols-2' : 'grid gap-4'}>
        {PORTAL_READY ? (
          <section className={SECTION} aria-label={t('clients:portal.label')}>
            {/* plan gate: portal — the portal is a switch, not an app. When the portal
              ships for real, gate this card on the tenant feature the same way
              useFeatureAccess gates /servicetitan, and leave the toggle visible-but-locked
              for tenants without it. */}
            <h2 className={SECTION_HEADING}>{t('clients:portal.label')}</h2>
            {/* The kit's Switch hardcodes its own <label>, so the 44px target floor
              (WCAG 2.5.8) has to be grown from here — measured at 24px otherwise. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 [&>label]:min-h-11">
              <Switch
                checked={customer.portal_enabled}
                disabled={update.isPending}
                onChange={(event) => togglePortal(event.target.checked)}
                label={t('clients:portal.toggle')}
              />
              <span className="text-muted-foreground text-xs">{t('clients:portal.lastSeen')}</span>
            </div>
            {update.isError ? (
              <div
                role="alert"
                className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
              >
                {t('clients:portal.saveError')}
              </div>
            ) : null}
          </section>
        ) : null}

        <section className={SECTION} aria-label={t('clients:detail.notes')}>
          <h2 className={SECTION_HEADING}>{t('clients:detail.notes')}</h2>
          {customer.notes ? (
            <p className="whitespace-pre-line text-foreground text-sm">{customer.notes}</p>
          ) : (
            <p className={EMPTY}>{t('clients:detail.noNotes')}</p>
          )}
        </section>
      </div>

      <section className={`${SECTION} mt-8`} aria-label={t('clients:detail.estimates')}>
        <h2 className={SECTION_HEADING}>{t('clients:detail.estimates')}</h2>
        {documents.estimates.length > 0 ? (
          <DocumentTable
            caption={t('clients:detail.estimatesCaption')}
            documents={documents.estimates}
            fmt={fmt}
            route="/estimates/$id"
            statusNamespace="estimates"
          />
        ) : (
          <p className={EMPTY}>{t('clients:detail.noEstimates')}</p>
        )}
      </section>

      <section className={`${SECTION} mt-4`} aria-label={t('clients:detail.invoices')}>
        <h2 className={SECTION_HEADING}>{t('clients:detail.invoices')}</h2>
        {documents.invoices.length > 0 ? (
          <DocumentTable
            caption={t('clients:detail.invoicesCaption')}
            documents={documents.invoices}
            fmt={fmt}
            route="/invoices/$id"
            statusNamespace="invoices"
          />
        ) : (
          <p className={EMPTY}>{t('clients:detail.noInvoices')}</p>
        )}
      </section>

      <section className={`${SECTION} mt-4`} aria-label={t('clients:detail.jobs')}>
        <h2 className={SECTION_HEADING}>{t('clients:detail.jobs')}</h2>
        {documents.jobs.length > 0 ? (
          <ul className="divide-border divide-y">
            {documents.jobs.map((job) => (
              <li key={job.id}>
                <JobRow job={job} />
              </li>
            ))}
          </ul>
        ) : (
          <p className={EMPTY}>{t('clients:detail.noJobs')}</p>
        )}
      </section>
    </div>
  );
}

function ContactItem({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className={FIELD_LABEL}>{label}</dt>
      <dd className="text-foreground text-sm">{value ?? '—'}</dd>
    </div>
  );
}

/** One estimates/invoices row: document number (link) · status · total (cents). */
function DocumentTable({
  caption,
  documents,
  fmt,
  route,
  statusNamespace,
}: {
  caption: string;
  documents: CustomerDocumentRef[];
  fmt: ReturnType<typeof useMoneyFormatters>;
  route: '/estimates/$id' | '/invoices/$id';
  /** Estimates and invoices each own their status labels; reuse them rather than copy. */
  statusNamespace: 'estimates' | 'invoices';
}) {
  const { t } = useTranslation();
  return (
    <ResponsiveTable>
      <table className="w-full border-collapse">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TH}>
              {t('clients:detail.docNumber')}
            </th>
            <th scope="col" className={TH}>
              {t('clients:detail.docStatus')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('clients:detail.docTotal')}
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 text-sm">
                <Link
                  to={route}
                  params={{ id: String(doc.id) }}
                  className="font-mono font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {doc.number || t('clients:detail.docUnnamed', { id: doc.id })}
                </Link>
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {t(`${statusNamespace}:status.${doc.status}`, { defaultValue: doc.status })}
              </td>
              <td className="px-3 py-2 text-right text-foreground text-sm tabular-nums">
                {fmt.formatCents(doc.total_cents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}

/** Job statuses are stored as labels ("On Hold"); the jobs locale keys them camelCase. */
// `jobs.status` is nullable in the database even though the type says string.
function jobStatusKey(status: string | null | undefined): string {
  if (!status) return '';
  return status === 'On Hold' ? 'onHold' : status.toLowerCase();
}

function JobRow({ job }: { job: CustomerJobRef }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
      <Link
        to="/jobs/$id"
        params={{ id: String(job.id) }}
        className="font-semibold text-foreground text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {job.name || t('clients:detail.jobUnnamed', { id: job.id })}
      </Link>
      <span className="text-muted-foreground text-sm">
        {job.status
          ? t(`jobs:status.${jobStatusKey(job.status)}`, { defaultValue: job.status })
          : null}
      </span>
    </div>
  );
}
